import { BLOG_POSTS, type BlogPost } from "../blogContent";

function slugify(value: string) {
	return value
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-+|-+$/g, "");
}

export function BlogPostPage({ post }: { post: BlogPost }) {
	const postIndex = BLOG_POSTS.findIndex((item) => item.href === post.href);
	const relatedPosts = Array.from(
		{ length: Math.min(3, BLOG_POSTS.length - 1) },
		(_, offset) => BLOG_POSTS[(postIndex + offset + 1) % BLOG_POSTS.length],
	);
	const tableOfContents = post.sections.map((section) => ({
		id: slugify(section.title),
		title: section.title,
	}));

	return (
		<section className="pb-24 pt-28 md:pb-32 md:pt-40">
			<div className="mx-auto max-w-6xl px-6">
				<div className="grid gap-12 lg:grid-cols-[0.32fr_0.68fr] lg:items-start">
					<aside className="lg:sticky lg:top-24">
						<a
							href="/blog/"
							className="text-sm font-medium text-brand-300 transition-colors hover:text-brand-200"
						>
							← Back to blog
						</a>
						<div className="mt-6 rounded-[1.5rem] border border-white/[0.08] bg-white/[0.03] p-5">
							<p className="text-[11px] uppercase tracking-[0.18em] text-white/35">In this guide</p>
							<div className="mt-4 space-y-3">
								{tableOfContents.map((item) => (
									<a
										key={item.id}
										href={`#${item.id}`}
										className="block text-sm leading-6 text-white/55 transition-colors hover:text-white"
									>
										{item.title}
									</a>
								))}
							</div>
						</div>
					</aside>

					<article>
						<div className="rounded-[2rem] border border-white/[0.08] bg-white/[0.04] p-6 shadow-2xl shadow-black/20 md:p-8">
							<div className="flex flex-wrap items-center gap-3 text-xs text-white/40">
								<span className="rounded-full border border-brand-500/20 bg-brand-500/10 px-3 py-1 text-brand-300">
									{post.heroEyebrow}
								</span>
								<span>{post.category}</span>
								<span>•</span>
								<span>{post.readTime}</span>
								<span>•</span>
								<span>{post.updatedLabel}</span>
							</div>
							<p className="mt-4 text-sm text-white/45">
								By{" "}
								<a
									href="/about/"
									className="text-brand-300 underline decoration-brand-300/30 underline-offset-2 hover:text-brand-200"
								>
									Clapio
								</a>{" "}
								— read our editorial standards and commercial disclosure.
							</p>
							<h1 className="mt-6 text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl md:text-6xl">
								{post.title}
							</h1>
							<p className="mt-6 max-w-3xl text-lg leading-relaxed text-white/58 md:text-xl">
								{post.description}
							</p>
							<p className="mt-6 max-w-3xl text-base leading-8 text-white/50 md:text-lg">
								{post.intro}
							</p>
						</div>

						{post.rankedTools?.length ? (
							<div className="mt-8 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
								{post.rankedTools.map((tool) => (
									<div
										key={`${tool.rank}-${tool.name}`}
										className={`rounded-[1.5rem] border p-5 ${tool.featured ? "border-brand-500/30 bg-brand-500/10" : "border-white/[0.08] bg-white/[0.03]"}`}
									>
										<div className="text-sm font-semibold text-brand-300">{tool.rank}</div>
										<h2 className="mt-3 text-xl font-semibold tracking-tight text-white/90">
											{tool.name}
										</h2>
										<p className="mt-2 text-sm font-medium text-white/65">{tool.bestFor}</p>
										<p className="mt-4 text-sm leading-7 text-white/48">{tool.description}</p>
									</div>
								))}
							</div>
						) : null}

						<div className="mt-10 space-y-12">
							{post.sections.map((section) => (
								<section key={section.title} id={slugify(section.title)}>
									<h2 className="text-2xl font-bold tracking-tight text-white md:text-3xl">
										{section.title}
									</h2>
									<div className="mt-5 space-y-5 text-base leading-8 text-white/52 md:text-lg">
										{section.paragraphs.map((paragraph) => (
											<p key={paragraph}>{paragraph}</p>
										))}
									</div>
									{section.bullets?.length ? (
										<ul className="mt-6 space-y-3 rounded-[1.5rem] border border-white/[0.08] bg-white/[0.03] p-5 text-sm leading-7 text-white/55 md:text-base">
											{section.bullets.map((bullet) => (
												<li key={bullet} className="flex gap-3">
													<span className="mt-2 h-1.5 w-1.5 rounded-full bg-brand-400" />
													<span>{bullet}</span>
												</li>
											))}
										</ul>
									) : null}
								</section>
							))}
						</div>

						<section className="mt-14 rounded-[2rem] border border-white/[0.08] bg-white/[0.03] p-6 md:p-8">
							<p className="text-[11px] uppercase tracking-[0.18em] text-brand-300">FAQ</p>
							<h2 className="mt-3 text-2xl font-semibold tracking-tight text-white/90 md:text-3xl">
								Questions readers usually ask next
							</h2>
							<div className="mt-6 space-y-4">
								{post.faqs.map((faq) => (
									<article
										key={faq.question}
										className="rounded-[1.4rem] border border-white/[0.08] bg-black/10 p-5"
									>
										<h3 className="text-lg font-semibold tracking-tight text-white/90">
											{faq.question}
										</h3>
										<p className="mt-3 text-sm leading-7 text-white/48 md:text-base">
											{faq.answer}
										</p>
									</article>
								))}
							</div>
						</section>

						{post.sources?.length ? (
							<section className="mt-10 rounded-[1.5rem] border border-white/[0.08] bg-white/[0.02] p-6">
								<h2 className="text-lg font-semibold tracking-tight text-white/90">
									Sources checked August 30, 2026
								</h2>
								<p className="mt-2 text-sm leading-7 text-white/45">
									Features, platform support, prices, and technical guidance can change. These are
									the official sources used for this article.
								</p>
								<div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm">
									{post.sources.map((source) => (
										<a
											key={source.href}
											href={source.href}
											target="_blank"
											rel="noopener noreferrer"
											className="text-brand-300 underline decoration-brand-300/30 underline-offset-2 hover:text-brand-200"
										>
											{source.label}
										</a>
									))}
								</div>
							</section>
						) : null}

						<div className="mt-14 grid gap-4 lg:grid-cols-[0.9fr_1.1fr]">
							<div className="glass-card p-6 md:p-8">
								<p className="text-[11px] uppercase tracking-[0.18em] text-white/35">
									Keep exploring
								</p>
								<h2 className="mt-3 text-2xl font-semibold tracking-tight text-white/90">
									More pages built for this search intent
								</h2>
								<p className="mt-4 text-sm leading-7 text-white/48 md:text-base">
									Use these related resources to compare alternatives, go deeper on your platform,
									or move from research to download.
								</p>
							</div>
							<div className="grid gap-3 md:grid-cols-2">
								{post.resources.map((resource) => (
									<a
										key={resource.href}
										href={resource.href}
										className="glass-card rounded-[1.4rem] px-5 py-5 transition-all hover:-translate-y-0.5 hover:border-brand-500/25"
									>
										<span className="block text-sm font-semibold text-white/90">
											{resource.label}
										</span>
										<span className="mt-2 block text-sm leading-7 text-white/45">
											{resource.description}
										</span>
									</a>
								))}
							</div>
						</div>

						<section className="mt-14 rounded-[2rem] border border-brand-500/20 bg-brand-500/10 p-6 md:p-8">
							<p className="text-[11px] uppercase tracking-[0.18em] text-brand-300">Try Clapio</p>
							<h2 className="mt-3 text-2xl font-semibold tracking-tight text-white md:text-3xl">
								{post.ctaTitle}
							</h2>
							<p className="mt-4 max-w-2xl text-sm leading-7 text-white/65 md:text-base">
								{post.ctaDescription}
							</p>
							<div className="mt-6 flex flex-wrap gap-3">
								<a
									href="/#download"
									className="rounded-xl bg-brand-500 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-brand-400"
								>
									Download Clapio
								</a>
								<a
									href="/#pricing"
									className="rounded-xl border border-white/[0.08] bg-white/[0.04] px-5 py-3 text-sm font-medium text-white/80 transition-colors hover:bg-white/[0.08]"
								>
									See pricing
								</a>
							</div>
						</section>

						{relatedPosts.length ? (
							<section className="mt-14">
								<h2 className="text-2xl font-semibold tracking-tight text-white md:text-3xl">
									More 2026 guides
								</h2>
								<div className="mt-6 grid gap-4 md:grid-cols-2">
									{relatedPosts.map((relatedPost) => (
										<a
											key={relatedPost.href}
											href={relatedPost.href}
											className="glass-card rounded-[1.5rem] p-6 transition-all hover:-translate-y-0.5 hover:border-brand-500/25"
										>
											<div className="text-xs text-white/40">{relatedPost.category}</div>
											<h3 className="mt-3 text-xl font-semibold tracking-tight text-white/90">
												{relatedPost.title}
											</h3>
											<p className="mt-4 text-sm leading-7 text-white/48">
												{relatedPost.description}
											</p>
										</a>
									))}
								</div>
							</section>
						) : null}
					</article>
				</div>
			</div>
		</section>
	);
}
